/**
 * Elite Realty 04 — Centre stage, carded.
 *
 * Symmetrical, and everything derives from the vertical centre line: a hairline
 * frame with brackets, a dot field behind an arch-cut photograph that sits on
 * its own lifted card, a gold stamp breaking the arch's foot, one centred row
 * of disc-borne figures, and the price on a gold plate.
 */

import { CANVAS, SPACE, RULE, RADIUS, SHADOW } from '../system/tokens';
import { getPalette, scrim } from '../system/palettes';
import { roles } from '../system/typography';
import { text, shape, rule, image, page } from '../system/elements';
import { getIcon } from '../system/icons';
import { PHOTOS } from '../system/photos';
import { BRAND, SPECS } from './system';
import { card, iconDisc, dotGrid, hatch, stamp, cornerBrackets } from './decor';

const ID = 'tpl-elite-flyer-04';

export default {
  id: ID,
  meta: {
    name: 'Elite — Modern Luxury',
    collection: 'elite-realty',
    archetype: 'centred-arch',
    documentType: 'Flyer',
    industry: 'Real Estate',
    subCategory: 'Real Estate',
    canvas: CANVAS.usLetterPortrait,
    palette: 'elite-navy-gold',
    pairing: 'elite-signature',
    tags: ['real estate', 'flyer', 'luxury', 'symmetric', 'arch', 'classic', 'bone', 'us letter'],
    description: 'A symmetrical centred sheet: hairline frame and brackets, a dot field behind a carded arch-cut photograph, a gold stamp at its foot and one centred row of figures.',
  },

  build() {
    const canvas = CANVAS.usLetterPortrait;
    const p = getPalette('elite-navy-gold');
    const r = roles('elite-signature');
    const els = [];

    const CX = canvas.width / 2;
    const COL = 1980;
    const colX = Math.round(CX - COL / 2);
    const centred = () => ({ x: colX, width: COL, align: 'center' });

    els.push(shape(`${ID}-field`, { x: 0, y: 0, width: canvas.width, height: canvas.height, fill: p.surface }));

    // Hairline frame.
    const INSET = 96;
    els.push(shape(`${ID}-frame-t`, { x: INSET, y: INSET, width: canvas.width - INSET * 2, height: RULE.thin, fill: p.accent }));
    els.push(shape(`${ID}-frame-b`, { x: INSET, y: canvas.height - INSET, width: canvas.width - INSET * 2, height: RULE.thin, fill: p.accent }));
    els.push(shape(`${ID}-frame-l`, { x: INSET, y: INSET, width: RULE.thin, height: canvas.height - INSET * 2, fill: p.accent }));
    els.push(shape(`${ID}-frame-r`, { x: canvas.width - INSET, y: INSET, width: RULE.thin, height: canvas.height - INSET * 2, fill: p.accent }));

    // ── Masthead ────────────────────────────────────────────────────────────
    let y = 200;
    els.push(...iconDisc(`${ID}-logo`, {
      x: Math.round(CX - 58), y, size: 116, content: getIcon('roofMark'),
      discFill: p.bg, iconColor: p.accent, ringColor: scrim(p.surfaceInk, 0.28),
    }));
    y += 152;

    els.push(text(`${ID}-logo-name`, r.h2, BRAND.name, { ...centred(), y },
      { color: p.surfaceInk, fontSize: 74, letterSpacing: 24 }));
    y += 96;
    els.push(text(`${ID}-logo-sub`, r.statLabel, BRAND.sub, { ...centred(), y },
      { color: p.accentDeep, fontSize: 26, letterSpacing: 13 }));
    y += 70;
    els.push(rule(`${ID}-rule-top`, { x: CX - 100, y, width: 200, weight: RULE.hair, color: p.accent }));
    y += SPACE.lg;

    // ── Arch photograph on a card, over a dot field ────────────────────────
    const PW = 1620;
    const PH = 1320;
    const px = Math.round(CX - PW / 2);

    els.push(dotGrid(`${ID}-dots`, {
      x: px - 168, y: y + 220, width: 150, height: PH - 520,
      color: scrim(p.surfaceInk, 0.24), step: 38, radius: 6,
    }));
    els.push(shape(`${ID}-arch-mount`, {
      x: px - SPACE.xxs, y: y - SPACE.xxs, width: PW + SPACE.xxs * 2, height: PH + SPACE.xxs * 2,
      fill: p.accent, cornerRadius: 0,
    }));
    els[els.length - 1].cornerRadii = {
      tl: (PW + SPACE.xxs * 2) / 2, tr: (PW + SPACE.xxs * 2) / 2, br: 0, bl: 0,
    };
    els.push(image(`${ID}-hero`, {
      x: px, y, width: PW, height: PH, src: PHOTOS.modernWhiteVilla,
      cornerRadii: { tl: PW / 2, tr: PW / 2, br: 0, bl: 0 },
    }));

    els.push(...stamp(`${ID}`, r, {
      x: px + PW - 250, y: y + PH - 210, size: 320, fill: p.accent, inkColor: p.accentInk, rotation: -10,
      lines: [
        { copy: 'JUST', size: 60, tracking: 4 },
        { copy: 'LISTED', size: 60, tracking: 4 },
      ],
    }));
    y += PH + SPACE.xl;

    // ── Centred lockup ──────────────────────────────────────────────────────
    els.push(text(`${ID}-eyebrow`, r.statLabel, 'Modern luxury · Austin, TX', { ...centred(), y },
      { color: p.accentDeep, fontSize: 32 }));
    y += 58;

    els.push(text(`${ID}-headline`, r.display, 'FOR SALE', { ...centred(), y },
      { color: p.surfaceInk, fontSize: 158 }));
    y += 158 * 0.94 + SPACE.xs;

    els.push(text(`${ID}-addr`, r.body, `${BRAND.address[0]}, ${BRAND.address[1]}`, { ...centred(), y },
      { color: p.surfaceInkMuted, fontSize: 42 }));
    y += 42 * 1.5 + SPACE.md;

    // ── One centred row of figures on discs ────────────────────────────────
    els.push(rule(`${ID}-spec-rule-top`, { x: colX, y, width: COL, weight: RULE.hair, color: scrim(p.surfaceInk, 0.3) }));
    y += SPACE.sm;

    const gap = SPACE.md;
    const cw = Math.floor((COL - gap * (SPECS.length - 1)) / SPECS.length);
    SPECS.forEach((s, i) => {
      const sx = colX + i * (cw + gap);
      els.push(...iconDisc(`${ID}-spec${i}`, {
        x: Math.round(sx + cw / 2 - 34), y, size: 68, content: getIcon(s.icon),
        discFill: p.bg, iconColor: p.accent,
      }));
      els.push(text(`${ID}-spec${i}-value`, r.h3, s.value, { x: sx, y: y + 88, width: cw, align: 'center' },
        { color: p.surfaceInk, fontSize: 56 }));
      els.push(text(`${ID}-spec${i}-label`, r.statLabel, s.label, { x: sx, y: y + 166, width: cw, align: 'center' },
        { color: p.surfaceInkMuted, fontSize: 25 }));
    });
    y += 214;
    els.push(rule(`${ID}-spec-rule-bot`, { x: colX, y, width: COL, weight: RULE.hair, color: scrim(p.surfaceInk, 0.3) }));
    y += SPACE.md;

    // ── Price on a gold plate ──────────────────────────────────────────────
    const PLW = 760;
    els.push(shape(`${ID}-price-plate`, {
      x: Math.round(CX - PLW / 2), y, width: PLW, height: 140, fill: p.accent, cornerRadius: RADIUS.sm,
    }));
    els.push(text(`${ID}-price`, r.h1, '$789,000', {
      x: Math.round(CX - PLW / 2), y: y + 18, width: PLW, align: 'center',
    }, { color: p.accentInk, fontSize: 92 }));
    y += 140 + SPACE.sm;

    els.push(text(`${ID}-script`, r.scriptSm, 'By appointment', { ...centred(), y },
      { color: p.surfaceInk, fontSize: 76 }));
    y += 76 + SPACE.xs;

    els.push(text(`${ID}-contact`, r.caption, `${BRAND.agent}  ·  ${BRAND.phone}  ·  ${BRAND.web}`, { ...centred(), y },
      { color: p.surfaceInkMuted, fontSize: 30 }));

    els.push(hatch(`${ID}-hatch`, {
      x: INSET, y: canvas.height - INSET - 16, width: canvas.width - INSET * 2, height: 16,
      color: p.accent, step: 20, weight: 5,
    }));
    els.push(...cornerBrackets(`${ID}`, {
      x: 140, y: 140, width: canvas.width - 280, height: canvas.height - 280,
      arm: 100, weight: RULE.medium, color: p.accent,
    }));

    return {
      canvas,
      pages: [page(`${ID}-page-1`, { name: 'Flyer', background: p.surface, elements: els })],
    };
  },
};
